import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ApiService from '../../helpers/apiService';
import { useProject } from '../../contexts/ProjectContext';

// Раздел «Документация» — per-project дерево доков из common_docs (wiki), проксируемое
// admin-api (/docs/v1/manifest, /docs/v1/page) согласно контракту
// common_docs/docs/team/admin-integration.md. Конфиг (base_url/slug/manifest_path) —
// в service_connect.data.docs текущего проекта. Гейт — projectDocsView.
//
// Тело документа — HTML, сгенерированный MkDocs. Рендерим в sandbox-iframe (srcDoc):
// изоляция CSS/JS от админки. Ассеты MkDocs (CSS/картинки) живут за forward-auth и
// браузеру недоступны → контент показывается без оформления wiki (читаемо). Прокси
// ассетов — follow-up (DOCS-2).

interface DocNode {
  title: string;
  path: string | null;
  category?: string;
  children?: DocNode[];
}

const DocsTree: React.FC<{
  nodes: DocNode[];
  depth: number;
  selectedPath: string | null;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onSelect: (node: DocNode) => void;
}> = ({ nodes, depth, selectedPath, expanded, onToggle, onSelect }) => (
  <ul className="list-unstyled mb-0">
    {nodes.map((node, i) => {
      const key = `${depth}:${node.path || node.title}:${i}`;
      const hasChildren = !!(node.children && node.children.length);
      const isOpen = expanded.has(key);
      const isLeaf = !!node.path;
      const isActive = isLeaf && node.path === selectedPath;
      return (
        <li key={key}>
          <div
            className={`d-flex align-items-center py-1 px-2 rounded ${isActive ? 'bg-primary text-white' : ''}`}
            style={{ cursor: 'pointer', paddingLeft: 8 + depth * 12 }}
            onClick={() => {
              if (hasChildren) onToggle(key);
              if (isLeaf) onSelect(node);
            }}
          >
            {hasChildren ? (
              <FontAwesomeIcon
                icon={isOpen ? 'chevron-down' : 'chevron-right'}
                className="me-2 text-500"
                style={{ fontSize: 10 }}
              />
            ) : (
              <FontAwesomeIcon
                icon="file-alt"
                className={`me-2 ${isActive ? 'text-white' : 'text-500'}`}
                style={{ fontSize: 11 }}
              />
            )}
            <span className="small text-truncate">{node.title}</span>
          </div>
          {hasChildren && isOpen && (
            <DocsTree
              nodes={node.children as DocNode[]}
              depth={depth + 1}
              selectedPath={selectedPath}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          )}
        </li>
      );
    })}
  </ul>
);

const DocsPage: React.FC = () => {
  const { currentProjectId, currentProject } = useProject() as any;

  const [manifest, setManifest] = useState<DocNode | null>(null);
  const [manifestMsg, setManifestMsg] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<DocNode | null>(null);

  const [pageHtml, setPageHtml] = useState<string>('');
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string>('');

  const [search, setSearch] = useState('');
  const reqSeq = useRef(0);

  const loadManifest = useCallback(() => {
    setLoading(true);
    setManifest(null);
    setManifestMsg('');
    setSelected(null);
    setPageHtml('');
    setPageError('');
    ApiService.docsGetManifest()
      .then(({ manifest: m, message }) => {
        setManifest(m);
        if (!m) setManifestMsg(message || 'Документация для проекта не настроена.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (currentProjectId) loadManifest();
  }, [currentProjectId, loadManifest]);

  const handleSelect = useCallback((node: DocNode) => {
    if (!node.path) return;
    setSelected(node);
    setPageLoading(true);
    setPageError('');
    setPageHtml('');
    const seq = ++reqSeq.current;
    ApiService.docsGetPage(node.path)
      .then(html => {
        if (seq !== reqSeq.current) return; // устаревший ответ
        setPageHtml(html || '');
      })
      .catch(err => {
        if (seq !== reqSeq.current) return;
        setPageError(err?.response?.data?.message || err?.message || 'Не удалось загрузить документ');
      })
      .finally(() => {
        if (seq === reqSeq.current) setPageLoading(false);
      });
  }, []);

  const handleToggle = useCallback((key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      // eslint-disable-next-line no-unused-expressions
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  // Поиск: плоский список листьев по подстроке заголовка.
  const filteredLeaves = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !manifest) return null;
    const out: DocNode[] = [];
    const walk = (nodes?: DocNode[]) => {
      (nodes || []).forEach(n => {
        if (n.path && n.title.toLowerCase().includes(q)) out.push(n);
        walk(n.children);
      });
    };
    walk(manifest.children);
    return out;
  }, [search, manifest]);

  const projectName = currentProject?.name || '';

  return (
    <>
      <Card className="mb-3">
        <Card.Body className="py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h5 className="mb-1">
              <FontAwesomeIcon icon="book" className="me-2 text-primary" />
              Документация
            </h5>
            <p className="mb-0 text-600 small">
              Документация проекта{projectName ? <> · <Badge bg="primary">{projectName}</Badge></> : ''} — бизнес-аналитика,
              система, сценарии, технические данные.
            </p>
          </div>
        </Card.Body>
      </Card>

      {!currentProjectId && (
        <Alert variant="warning">Выберите проект, чтобы открыть его документацию.</Alert>
      )}

      {currentProjectId && (
        <div className="row g-3">
          {/* Дерево */}
          <div className="col-12 col-lg-4 col-xl-3">
            <Card style={{ position: 'sticky', top: 16 }}>
              <Card.Header className="py-2">
                <Form.Control
                  size="sm"
                  type="search"
                  placeholder="Поиск по документам…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  aria-label="Поиск по документации"
                />
              </Card.Header>
              <Card.Body className="p-2" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading && (
                  <div className="text-center py-4 text-600">
                    <Spinner animation="border" size="sm" className="me-2" /> Загрузка…
                  </div>
                )}
                {!loading && !manifest && (
                  <div className="text-600 small p-2">{manifestMsg || 'Нет данных.'}</div>
                )}
                {!loading && manifest && filteredLeaves && (
                  <ul className="list-unstyled mb-0">
                    {filteredLeaves.length === 0 && <li className="text-600 small p-2">Ничего не найдено.</li>}
                    {filteredLeaves.map((n, i) => (
                      <li key={`s:${n.path}:${i}`}>
                        <div
                          className={`d-flex align-items-center py-1 px-2 rounded ${
                            selected?.path === n.path ? 'bg-primary text-white' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelect(n)}
                        >
                          <FontAwesomeIcon icon="file-alt" className="me-2 text-500" style={{ fontSize: 11 }} />
                          <span className="small text-truncate">{n.title}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {!loading && manifest && !filteredLeaves && (
                  <DocsTree
                    nodes={manifest.children || []}
                    depth={0}
                    selectedPath={selected?.path || null}
                    expanded={expanded}
                    onToggle={handleToggle}
                    onSelect={handleSelect}
                  />
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Контент */}
          <div className="col-12 col-lg-8 col-xl-9">
            <Card style={{ minHeight: '60vh' }}>
              <Card.Body className="p-0 d-flex flex-column">
                {!selected && !pageLoading && (
                  <div className="text-center text-600 py-5 my-auto">
                    <FontAwesomeIcon icon="book-open" size="2x" className="mb-3 text-300" />
                    <p className="mb-0">Выберите документ слева, чтобы открыть его.</p>
                  </div>
                )}
                {pageLoading && (
                  <div className="text-center py-5 my-auto">
                    <Spinner animation="border" /> <span className="ms-2">Загрузка документа…</span>
                  </div>
                )}
                {!pageLoading && pageError && (
                  <Alert variant="danger" className="m-3">
                    {pageError}
                  </Alert>
                )}
                {!pageLoading && !pageError && selected && (
                  <iframe
                    title={selected.title}
                    sandbox=""
                    srcDoc={pageHtml}
                    style={{ width: '100%', height: '75vh', border: 'none', borderRadius: 6 }}
                  />
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};

export default DocsPage;
